import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import styled from "styled-components";
import { captureException } from "@sentry/react";
import { v5 } from "@credijusto/minerals"; // TODO: Actualizar version de minerals
import styles from "./LoginForm.module.css";
import { useHistory } from "react-router-dom";
import { useAuth } from "../AuthContext/AuthContext";
import ReCAPTCHA from "react-google-recaptcha";

const Card = styled.div`
  width: 100%;
  max-width: 1000px;
  box-sizing: border-box;
  z-index: 1;
  margin: 0 auto;

  @media screen and (min-width: 767px) {
    margin-left: 0.1rem;
    margin-right: auto;
    padding-right: 0;
    max-width: 50%;
  }

  @media screen and (min-width: 1024px) {
    margin-left: 0.5rem;
    max-width: 45%;
  }

  @media screen and (min-width: 1440px) {
    margin-left: -1rem;
    max-width: 45%;
  }
`;

const LoginWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;

  @media screen and (min-width: 768px) {
    gap: 1.5rem;
    padding: 2rem 1rem;
  }

  @media screen and (min-width: 1024px) {
    display: table-cell;
    gap: 2rem;
    padding: 2rem 3.5rem;
    vertical-align: middle;
  }

  @media screen and (min-width: 1440px) {
    padding: 0 5rem;
  }
`;

const AutorizeBureu = styled.a`
  font-weight: bold;
  color: mn-secondary;
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  cursor: pointer;
`;

const RecaptchaMessage = styled.div`
  text-align: center;
  font-size: 0.875rem;
  font-weight: ${(props) => (props.verified ? "bold" : "normal")};
  margin-top: 0.5rem;
  min-height: 20px;
  color: ${(props) => props.color};
  transition: color 0.3s ease;
`;

const popupCommonStyles = {
  borderRadius: "16px",
  maxWidth: "700px",
  padding: "2rem",
  textAlign: "center",
};

const validationRules = {
  rfc: [
    (value) => (value.length === 0 ? "El RFC es requerido" : null),
    (value) => {
      if (value.length === 0) return null;
      const rfcRegex =
        /^[A-ZÑ&]{3,4}(\d{2}(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))[A-Z0-9]{2}[0-9A-Z]?$/;
      return !rfcRegex.test(value.toUpperCase())
        ? "Formato de RFC inválido"
        : null;
    },
    (value) =>
      value.length < 12 || value.length > 13
        ? "El RFC debe tener 12 o 13 caracteres, incluyendo homoclave"
        : null,
  ],
  ciec: [
    (value) => (value.length === 0 ? "El CIEC es requerido" : null),
    (value) =>
      value.length !== 8 ? "El CIEC debe tener exactamente 8 caracteres" : null,
    (value) => {
      if (value.length === 0) return null;
      const ciecRegex = /^[a-zA-Z0-9&]+$/;
      return !ciecRegex.test(value)
        ? "El CIEC solo puede contener letras (a-z), números (0-9) y el carácter &"
        : null;
    },
  ],
  tac: [
    (value) =>
      !value ? "Es necesario que acepte la política de privacidad" : null,
  ],
  buro: [
    (value) =>
      !value
        ? "Es necesario que autorize la consulta del Buró de Crédito"
        : null,
  ],
};

const initialFormDataValues = {
  rfc: "",
  ciec: "",
  tac: false,
  buro: false,
};

const ErrorMsgComp = ({ error }) => (
  <p className="px-2 text-mn-caption1 leading-mn-caption1 tracking-mn-caption1 text-mn-error font-sans font-normal">
    {error}
  </p>
);

const apiRequest = async (payload) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_VISOR_SERVER}/webapi/validar_ciec`,
      payload,
      {
        timeout: 45000,
        validateStatus: (status) => status < 500,
      },
    );

    if (response.status >= 400) {
      const error = new Error(response.data?.error || "Error desconocido");
      error.response = {
        status: response.status,
        data: response.data || { error: "Error desconocido" },
      };
      throw error;
    }

    return response.data;
  } catch (error) {
    if (error.code === "ECONNABORTED") {
      const timeoutError = new Error(
        "La solicitud está tardando demasiado. Por favor intenta nuevamente.",
      );
      timeoutError.response = {
        status: 408,
        data: {
          error:
            "La solicitud está tardando demasiado. Por favor intenta nuevamente.",
        },
      };
      throw timeoutError;
    }

    if (!error.response) {
      const connectionError = new Error(
        "No se pudo conectar con el servidor. Verifica tu conexión a internet.",
      );
      connectionError.response = {
        status: 503,
        data: {
          error:
            "No se pudo conectar con el servidor. Verifica tu conexión a internet.",
        },
      };
      throw connectionError;
    }

    throw error;
  }
};

const LoginForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialFormDataValues);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successRfc, setSuccessRfc] = useState(""); // ← Nueva variable para el RFC del mensaje
  const [hasCiec, setHasCiec] = useState(null);
  const [errors, setErrors] = useState({
    rfc: null,
    ciec: null,
    tac: null,
    buro: null,
  });
  const [errorPopup, setErrorPopup] = useState({
    open: false,
    title: "",
    message: "",
    type: "error",
  });
  const [rfcValidation, setRfcValidation] = useState({
    isValid: false,
    message: null,
    isTyping: false,
  });
  const [recaptcha, setRecaptcha] = useState(null);
  const [recaptchaStatus, setRecaptchaStatus] = useState({
    verified: false,
    message: "Marca la casilla para verificar que no eres un robot",
    color: "red",
  });
  const [showCiecRequired, setShowCiecRequired] = useState(false);
  const [showModal, setShowModal] = useState(false); // ← Agrega esta línea
  const recaptchaRef = useRef(null);
  const rfcTimeoutRef = useRef(null);

  const history = useHistory();
  const { setRfc, setTacAccepted, setBuroAuthorized } = useAuth();

  const showErrorPopup = useCallback((title, message, type = "error") => {
    setErrorPopup({
      open: true,
      title,
      message,
      type,
    });
  }, []);

  const validate = useCallback((name, value) => {
    for (let i = 0; i < validationRules[name].length; i++) {
      const err = validationRules[name][i](value);
      if (err) return err;
    }
    return null;
  }, []);

  const validateRFC = useCallback((rfc) => {
    if (rfc.length === 0) {
      return { isValid: false, message: "El RFC es requerido" };
    }

    if (rfc.length < 12 || rfc.length > 13) {
      return {
        isValid: false,
        message: "El RFC debe tener 12 o 13 caracteres",
      };
    }

    const rfcRegex =
      /^[A-ZÑ&]{3,4}(\d{2}(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))[A-Z0-9]{2}[0-9A-Z]?$/;
    if (!rfcRegex.test(rfc.toUpperCase())) {
      return { isValid: false, message: "Formato de RFC inválido" };
    }

    return { isValid: true, message: null };
  }, []);

  const resetRecaptcha = useCallback(() => {
    setRecaptcha(null);
    setRecaptchaStatus({
      verified: false,
      message: "Marca la casilla para verificar que no eres un robot",
      color: "red",
    });
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.reset();
      } catch (error) {
        console.warn("Error al resetear reCAPTCHA:", error);
      }
    }
  }, []);

  // Nueva función para verificar si todos los campos requeridos están completos
  const checkAllFieldsComplete = useCallback(() => {
    // Validar RFC
    if (!formData.rfc || !rfcValidation.isValid) return false;

    // Si se requiere CIEC (RFC de 12 caracteres o selección explícita)
    const ciecRequired =
      hasCiec === true || (rfcValidation.isValid && formData.rfc.length === 12);

    if (ciecRequired) {
      return (
        formData.ciec &&
        formData.ciec.length === 8 &&
        formData.tac &&
        formData.buro
      );
    } else if (hasCiec === false) {
      // No requiere CIEC
      return formData.tac && formData.buro;
    }

    return false;
  }, [formData, rfcValidation, hasCiec]);

  // Efecto para actualizar mensajes de reCAPTCHA cuando cambian los campos
  useEffect(() => {
    if (recaptcha) {
      const allComplete = checkAllFieldsComplete();

      if (allComplete) {
        setRecaptchaStatus({
          verified: true,
          message:
            "¡Perfecto! ¿Ya completaste todos los datos?, ahora presiona Continuar.",
          color: "green",
        });
      } else {
        setRecaptchaStatus({
          verified: true,
          message: "Validación completada, pero faltan campos por completar",
          color: "red",
        });
      }
    }
  }, [formData, recaptcha, checkAllFieldsComplete]);

  const handleRfcChange = useCallback(
    (e) => {
      const { value } = e.target;
      const upperValue = value.toUpperCase();

      // Limpiar timeout anterior si existe
      if (rfcTimeoutRef.current) {
        clearTimeout(rfcTimeoutRef.current);
      }

      setFormData((prev) => ({
        ...prev,
        rfc: upperValue,
      }));

      // Mostrar estado de escritura
      setRfcValidation({
        isValid: false,
        message: null,
        isTyping: true,
      });

      // Validar solo cuando la longitud sea 12 o 13 caracteres
      if (upperValue.length === 12 || upperValue.length === 13) {
        rfcTimeoutRef.current = setTimeout(() => {
          const validation = validateRFC(upperValue);
          setRfcValidation({
            ...validation,
            isTyping: false,
          });

          // Si es válido y tiene 12 caracteres, forzar CIEC
          if (validation.isValid && upperValue.length === 12) {
            setHasCiec(true);
          }
        }, 800); // Esperar 800ms después de que el usuario deje de escribir
      } else {
        setRfcValidation({
          isValid: false,
          message: null,
          isTyping: false,
        });

        // Si no tiene la longitud adecuada, resetear la selección de CIEC
        if (hasCiec !== null) {
          setHasCiec(null);
        }
      }
    },
    [validateRFC, hasCiec],
  );

  // Modificar handleCiecChange para simplificarlo
  const handleCiecChange = useCallback((e) => {
    const { name, value } = e.target;
    const filteredValue = value.replace(/[^a-zA-Z0-9&]/g, "");
    const truncatedValue = filteredValue.slice(0, 8);

    setFormData((data) => ({ ...data, [name]: truncatedValue }));

    // Validar en tiempo real solo la longitud
    if (truncatedValue.length !== 8) {
      setErrors((prev) => ({
        ...prev,
        [name]: "El CIEC debe tener 8 caracteres",
      }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  }, []);

  const handleCiecBlur = useCallback((e) => {
    const { name, value } = e.target;
    const ciecRegex = /^[a-zA-Z0-9&]{8}$/;

    if (!ciecRegex.test(value)) {
      setErrors((prev) => ({
        ...prev,
        [name]:
          "El CIEC debe tener exactamente 8 caracteres alfanuméricos (a-z, 0-9) o &",
      }));
      // Mostrar mensaje de requerido solo si el campo está vacío
      setShowCiecRequired(value.length === 0);
    } else {
      setErrors((prev) => ({ ...prev, [name]: null }));
      setShowCiecRequired(false);
    }
  }, []);

  const validateBeforeSubmit = useCallback(() => {
    const ciecError = validate("ciec", formData.ciec);
    if (ciecError) {
      setErrors((prev) => ({ ...prev, ciec: ciecError }));
      return false;
    }
    return true;
  }, [formData.ciec, validate]);

  const handleRegistroSinCIEC = useCallback(async () => {
    if (!rfcValidation.isValid) {
      setErrors((prev) => ({ ...prev, rfc: rfcValidation.message }));
      return;
    }

    const tacError = validate("tac", formData.tac);
    const buroError = validate("buro", formData.buro);

    if (tacError || buroError) {
      setErrors((prev) => ({
        ...prev,
        tac: tacError,
        buro: buroError,
      }));
      return;
    }

    try {
      setRfc(formData.rfc);
      setTacAccepted(formData.tac);
      setBuroAuthorized(formData.buro);

      localStorage.setItem("rfc_temp", formData.rfc);
      localStorage.setItem("tac_accepted", formData.tac);
      localStorage.setItem("buro_authorized", formData.buro);

      history.push("/register");
    } catch (error) {
      resetRecaptcha();
      showErrorPopup(
        "Error",
        "Ocurrió un error al procesar tu solicitud. Por favor intenta nuevamente.",
        "error",
      );
    }
  }, [
    formData,
    rfcValidation,
    validate,
    setRfc,
    setTacAccepted,
    setBuroAuthorized,
    history,
    showErrorPopup,
    resetRecaptcha,
  ]);

  // Simplificar isReady
  const isReady = useCallback(() => {
    return (
      checkAllFieldsComplete() &&
      (!(
        hasCiec === true ||
        (rfcValidation.isValid && formData.rfc.length === 12)
      ) ||
        recaptcha)
    );
  }, [checkAllFieldsComplete, hasCiec, rfcValidation, formData.rfc, recaptcha]);

  // Modificar onInputChange para checkboxes
  const onInputChange = useCallback(
    (e) => {
      const { name, type, value, checked } = e.target;
      let finalValue = type === "checkbox" ? checked : value;
      if (name === "rfc") finalValue = finalValue.toUpperCase();

      setFormData((data) => ({
        ...data,
        [name]: finalValue,
      }));

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validate(name, finalValue),
      }));
    },
    [validate],
  );

  const handleRecaptchaChange = useCallback(
    (token) => {
      setRecaptcha(token);

      // Validar todos los campos requeridos cuando se marca el reCAPTCHA
      const newErrors = { ...errors };
      let hasNewErrors = false;

      // Validar CIEC si es requerido
      if (
        hasCiec === true ||
        (rfcValidation.isValid && formData.rfc.length === 12)
      ) {
        if (formData.ciec.length === 0) {
          newErrors.ciec = "El CIEC es requerido";
          hasNewErrors = true;
          setShowCiecRequired(true);
        } else {
          // Si hay CIEC, validar que tenga 8 caracteres
          const ciecError = validate("ciec", formData.ciec);
          if (ciecError) {
            newErrors.ciec = ciecError;
            hasNewErrors = true;
          } else {
            newErrors.ciec = null;
          }
        }
      }

      // Validar checkboxes
      const tacError = validate("tac", formData.tac);
      if (tacError) {
        newErrors.tac = tacError;
        hasNewErrors = true;
      } else {
        newErrors.tac = null;
      }

      const buroError = validate("buro", formData.buro);
      if (buroError) {
        newErrors.buro = buroError;
        hasNewErrors = true;
      } else {
        newErrors.buro = null;
      }

      // Actualizar errores si hay nuevos
      if (hasNewErrors) {
        setErrors(newErrors);
      }

      // Actualizar mensaje de reCAPTCHA
      const allComplete = checkAllFieldsComplete();
      if (hasNewErrors || !allComplete) {
        setRecaptchaStatus({
          verified: true,
          message: "Validación completada, pero faltan campos por completar",
          color: "red",
        });
      } else {
        setRecaptchaStatus({
          verified: true,
          message:
            "¡Perfecto! ¿Ya completaste todos los datos?, ahora presiona Continuar.",
          color: "green",
        });
        setShowCiecRequired(false);
      }
    },
    [
      formData,
      hasCiec,
      rfcValidation,
      validate,
      errors,
      checkAllFieldsComplete,
    ],
  );

  const handleRecaptchaExpired = useCallback(() => {
    setRecaptcha(null);
    setRecaptchaStatus({
      verified: false,
      message:
        "La verificación ha expirado. Por favor, marca la casilla nuevamente",
      color: "red",
    });
  }, []);

  const handleRecaptchaError = useCallback(() => {
    setRecaptcha(null);
    setRecaptchaStatus({
      verified: false,
      message: "Error en la verificación. Intenta nuevamente",
      color: "red",
    });
  }, []);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Validación mejorada del CIEC antes de enviar
      if (!validateBeforeSubmit()) {
        return;
      }

      // Validar reCAPTCHA solo si tiene CIEC
      if (
        (hasCiec === true ||
          (rfcValidation.isValid && formData.rfc.length === 12)) &&
        !recaptcha
      ) {
        showErrorPopup(
          "Verificación requerida",
          "Por favor completa la verificación reCAPTCHA",
          "error",
        );
        return;
      }

      const errs = { ...errors };
      Object.keys(formData).forEach((field) => {
        errs[field] = validate(field, formData[field]);
      });

      if (formData.rfc && !rfcValidation.isValid) {
        errs.rfc = rfcValidation.message;
      }

      setErrors(errs);

      if (Object.values(errs).some((error) => error !== null)) return;
      if (!isReady()) return;

      setIsSubmitting(true);

      try {
        const payload = {
          rfc: formData.rfc,
          ciec: formData.ciec,
          tac: formData.tac,
          buro: formData.buro,
          recaptcha:
            hasCiec === true ||
            (rfcValidation.isValid && formData.rfc.length === 12)
              ? recaptcha
              : null,
        };

        const response = await apiRequest(payload);

        if (response && response.ciec_valida !== undefined) {
          if (response.ciec_valida) {
            setRfc(formData.rfc);
            setTacAccepted(formData.tac);
            setBuroAuthorized(formData.buro);

            localStorage.setItem("rfc_temp", formData.rfc);
            localStorage.setItem("tac_accepted", formData.tac);
            localStorage.setItem("buro_authorized", formData.buro);

            setSuccessRfc(formData.rfc); // ← Guardar el RFC antes de mostrar éxito
            setShowSuccess(true);
          } else {
            showErrorPopup(
              "Credenciales incorrectas",
              `##### Credenciales incorrectas \nEl CIEC proporcionado no coincide con el RFC ${formData.rfc}. \n Por favor verifica tus datos.`,
              "error",
            );
            // Limpiar el campo CIEC cuando es incorrecto
            setFormData((prev) => ({
              ...prev,
              ciec: "",
            }));
            setErrors((prev) => ({
              ...prev,
              ciec: "CIEC incorrecta",
            }));
            // Resetear el reCAPTCHA
            resetRecaptcha();
          }
        } else {
          showErrorPopup(
            "Error en el servidor",
            "##### Error en el servidor \nNo pudimos validar tus credenciales. Por favor intenta más tarde.",
            "error",
          );
          resetRecaptcha();
        }
      } catch (err) {
        resetRecaptcha();

        if (err.response && err.response.status === 401) {
          showErrorPopup(
            "Credenciales incorrectas",
            `##### Credenciales incorrectas \nEl CIEC proporcionado no coincide con el RFC ${formData.rfc}. \n Por favor verifica tus datos.`,
            "error",
          );
          // Limpiar el campo CIEC cuando es incorrecto
          setFormData((prev) => ({
            ...prev,
            ciec: "",
          }));
          setErrors((prev) => ({
            ...prev,
            ciec: "CIEC incorrecta",
          }));
        } else {
          let title = "Error";
          let message =
            "##### Error \nHubo un problema al procesar tu solicitud.";
          let type = "error";

          if (err.response) {
            switch (err.response.status) {
              case 400:
                title = "Datos incorrectos";
                message =
                  err.response.data?.error ||
                  "##### Datos incorrectos \nPor favor verifica la información ingresada.";
                break;
              case 403:
                title = "Acceso no autorizado";
                message =
                  err.response.data?.error ||
                  "##### Acceso no autorizado \nCredenciales incorrectas o sesión expirada.";
                type = "warning";
                break;
              case 404:
                title = "Recurso no encontrado";
                message =
                  "##### Recurso no encontrado \nEl servicio solicitado no está disponible.";
                break;
              case 408:
                title = "Tiempo agotado";
                message =
                  "##### Tiempo agotado \nEl servidor no respondió a tiempo. Intenta nuevamente.";
                break;
              case 500:
                title = "Error del servidor";
                message =
                  "##### Error del servidor \nProblema interno del servidor. Por favor intenta más tarde.";
                break;
              case 503:
                title = "Servicio no disponible";
                message =
                  "##### Servicio no disponible \nEl servicio no está disponible temporalmente. Intenta más tarde.";
                break;
              default:
                message = err.response.data?.error || message;
            }
          } else {
            title = "Error de conexión";
            message =
              "##### Error de conexión \nNo se pudo establecer conexión con el servidor. Verifica tu conexión a internet.";
          }

          showErrorPopup(title, message, type);
        }
        captureException(err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      errors,
      isReady,
      rfcValidation,
      setRfc,
      setTacAccepted,
      setBuroAuthorized,
      validate,
      showErrorPopup,
      validateBeforeSubmit,
      hasCiec,
      recaptcha,
      resetRecaptcha,
    ],
  );

  useEffect(() => {
    if (showSuccess) {
      setFormData(initialFormDataValues);
      setErrors({
        rfc: null,
        ciec: null,
        tac: null,
        buro: null,
      });
      setHasCiec(null);
      setRecaptcha(null);
      setRecaptchaStatus({
        verified: false,
        message: "Marca la casilla para verificar que no eres un robot",
        color: "red",
      });
      // No resetear successRfc aquí
    }
  }, [showSuccess]);

  useEffect(() => {
    // Limpiar timeout al desmontar el componente
    return () => {
      if (rfcTimeoutRef.current) {
        clearTimeout(rfcTimeoutRef.current);
      }
    };
  }, []);

  const handleCloseErrorPopup = useCallback(() => {
    setErrorPopup((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <LoginWrapper>
      <Card>
        <v5.Typography
          color="mn-secondary"
          font="serif"
          variant="h3"
          weight="bold"
        >
          Impulsa tu futuro financiero
        </v5.Typography>

        <v5.Typography
          color="mn-text-grey"
          variant="subtitle1"
          weight="medium"
          style={{ marginBottom: "1rem" }}
        >
          Sólo necesitamos tu RFC para iniciar. Después te pediremos tu
          autorización para consultar tu historial crediticio. Todo el proceso
          es rápido y seguro.
        </v5.Typography>

        <form onSubmit={onSubmit}>
          <v5.Input
            label="RFC:"
            type="text"
            placeholder="Escribe tu RFC"
            name="rfc"
            value={formData.rfc}
            onChange={handleRfcChange}
            onBlur={() => {
              if (rfcTimeoutRef.current) {
                clearTimeout(rfcTimeoutRef.current);
              }
              setRfcValidation((prev) => ({ ...prev, isTyping: false }));

              // Forzar validación al perder el foco
              const validation = validateRFC(formData.rfc);
              setRfcValidation(validation);

              // Si es válido y tiene 12 caracteres, forzar CIEC
              if (validation.isValid && formData.rfc.length === 12) {
                setHasCiec(true);
              }
            }}
            maxLength={13}
            errorMessage={rfcValidation.isTyping ? null : rfcValidation.message}
            classNameContainer="mb-2"
          />

          {/* Mostrar select solo si el RFC es válido y no tiene 12 caracteres */}
          {rfcValidation.isValid && formData.rfc.length !== 12 && (
            <v5.Select
              name="hasCiec"
              placeholder="Selecciona"
              tooltipText="Contraseña emitida por el SAT que identifica al contribuyente y permite realizar trámites fiscales en línea"
              label="¿Tienes contraseña CIEC?"
              value={hasCiec === true ? "Sí" : hasCiec === false ? "No" : ""}
              onChangeSelect={(selected) => {
                const value = selected?.value;
                setHasCiec(value === "true");
                setRecaptcha(null);
                resetRecaptcha();
                if (value === "false") {
                  setErrors((prev) => ({ ...prev, ciec: null }));
                  setShowCiecRequired(false);
                } else if (value === "true") {
                  setShowCiecRequired(true);
                }
              }}
              options={[
                { label: "Sí", value: "true" },
                { label: "No", value: "false" },
              ]}
            />
          )}

          {/* Mostrar campo CIEC si:
              1. El RFC tiene 12 caracteres válidos (forzado)
              2. O si el usuario seleccionó "Sí" en el select */}
          {(hasCiec === true ||
            (rfcValidation.isValid && formData.rfc.length === 12)) && (
            <>
              <v5.Input
                label="Contraseña (CIEC):"
                type="password"
                name="ciec"
                placeholder="Escribe tu contraseña (CIEC)"
                maxLength={8}
                value={formData.ciec}
                onChange={handleCiecChange}
                onBlur={handleCiecBlur}
                errorMessage={errors.ciec}
                tooltip="Clave de Identificación Electrónica Confidencial (8 caracteres alfanuméricos o &)"
                autoComplete="off"
                classNameContainer="mb-2"
              />

              {/* Mensaje de error cuando falta CIEC pero hay reCAPTCHA o checks marcados */}
              {showCiecRequired && formData.ciec.length === 0 && (
                <p className="px-2 text-mn-caption1 leading-mn-caption1 tracking-mn-caption1 text-mn-error font-sans font-normal mb-2">
                  El CIEC es requerido para continuar
                </p>
              )}
            </>
          )}

          {/* Mostrar checkboxes solo si el RFC es válido */}
          {rfcValidation.isValid && (
            <>
              <v5.Checkbox
                id="tac"
                name="tac"
                checked={formData.tac}
                onChange={onInputChange}
                errorMessage={errors.tac}
                label={
                  <v5.Markdown className={styles.markdownSecondary}>
                    {`Acepto la
                    <a
                      href="https://www.covalto.com/privacidad/"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="${styles.termsLink}"
                    >
                      política de privacidad
                    </a>
                  `}
                  </v5.Markdown>
                }
              />
              {errors.tac && <ErrorMsgComp error={errors.tac} />}

              <v5.Checkbox
                id="buro"
                name="buro"
                checked={formData.buro}
                onChange={onInputChange}
                style={{ color: "red" }}
                errorMessage={errors.buro}
                label={
                  <AutorizeBureu
                    onClick={(e) => {
                      setShowModal(true);
                      e.preventDefault();
                    }}
                  >
                    Autorizo a BANCO COVALTO S.A., INSTITUCIÓN DE BANCA
                    MÚLTIPLE, la consulta de Buró de Crédito
                  </AutorizeBureu>
                }
              />
              {errors.buro && <ErrorMsgComp error={errors.buro} />}
            </>
          )}

          {/* reCAPTCHA solo para usuarios con CIEC */}
          {(hasCiec === true ||
            (rfcValidation.isValid && formData.rfc.length === 12)) && (
            <div
              style={{
                margin: "1rem 0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "0.5rem",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    position: "relative",
                  }}
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                    onErrored={handleRecaptchaError}
                  />
                </div>
              </div>
              <div
                style={{
                  width: "304px", // Ancho estándar del widget reCAPTCHA
                  maxWidth: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <RecaptchaMessage
                  color={recaptchaStatus.color}
                  verified={recaptchaStatus.verified}
                >
                  {recaptchaStatus.message}
                </RecaptchaMessage>
              </div>
            </div>
          )}

          {/* Mostrar botón de continuar sin CIEC solo si:
              1. El usuario seleccionó "No" en el select
              2. Y el RFC no tiene 12 caracteres (que forzarían CIEC) */}
          {hasCiec === false &&
            !(rfcValidation.isValid && formData.rfc.length === 12) && (
              <v5.Button
                variant="primary"
                onClick={handleRegistroSinCIEC}
                disabled={
                  !formData.tac || !formData.buro || !rfcValidation.isValid
                }
                className="mt-4 mb-6 w-full bg-white text-mn-primary border border-mn-primary hover:bg-mn-secondary hover:text-white transition-colors duration-300"
              >
                Continuar
              </v5.Button>
            )}

          {/* Mostrar botón de enviar formulario si:
              1. El usuario seleccionó "Sí" en el select
              2. O el RFC tiene 12 caracteres (que forzarían CIEC) */}
          {(hasCiec === true ||
            (rfcValidation.isValid && formData.rfc.length === 12)) && (
            <v5.Button
              variant="primary"
              type="submit"
              className="mt-4 mb-6 w-full bg-white text-mn-primary border border-mn-primary hover:bg-mn-secondary hover:text-white transition-colors duration-300"
              disabled={!isReady() || isSubmitting || !recaptcha}
            >
              {isSubmitting ? "Continuando..." : "Continuar"}
            </v5.Button>
          )}
        </form>
      </Card>

      <v5.PopUp
        successButtonText="Aceptar"
        className="w-full relative w-full max-w-[660px] bg-mn-neutral-five shadow-lg p-6 undefined opacity-100 animate-slideBottomIn popup"
        type="success"
        onSuccess={() => {
          setShowSuccess(false);
          setSuccessRfc(""); // ← Limpiar el RFC del mensaje aquí
          window.location.href = "/";
        }}
        open={showSuccess}
        style={popupCommonStyles}
      >
        {`##### Felicidades \nSe envió la información del RFC ${successRfc} y se validó con contraseña CIEC`}
      </v5.PopUp>

      <v5.PopUp
        open={errorPopup.open}
        onClose={handleCloseErrorPopup}
        title={errorPopup.title}
        type={errorPopup.type}
        successButtonText="Entendido"
        onSuccess={handleCloseErrorPopup}
        style={popupCommonStyles}
      >
        {errorPopup.message}
      </v5.PopUp>

      <v5.PopUp
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Autorización para solicitar Reportes de Crédito"
        style={{
          borderRadius: "16px",
          maxWidth: "800px",
          padding: "2rem",
        }}
      >
        {"**Autorización para solicitar Reportes de Crédito Personas Físicas / Personas Morales Empresas Comerciales** \n\n --- \n" +
          "Por este conducto autorizo expresamente a APJUSTO SAPI DE CV o BANCO COVALTO SA IBM o JUSTO LEASE SAPI DE CV, para que por conducto de sus funcionarios facultados lleve a cabo Investigaciones, sobre mi comportamiento crediticio o el de la Empresa que represento en Sociedades de Información Crediticia. \n\n " +
          "Así mismo, declaro que conozco la naturaleza y alcance de la información que se solicitará, del uso que APJUSTO SAPI DE CV o BANCO COVALTO SA IBM o JUSTO LEASE SAPI DE CV, hará de tal información y de que ésta podrá realizar consultas periódicas sobre mi historial o el de la empresa que represento, consintiendo que esta autorización se encuentre vigente por un período de 3 años contados a partir de su expedición y en todo caso durante el tiempo que se mantenga la relación jurídica. \n\n" +
          "En caso de que la solicitante sea una Persona Moral, declaro bajo protesta de decir verdad Ser Representante Legal de la empresa mencionada en esta autorización; manifestando que a la fecha de firma de la presente autorización los poderes no me han sido revocados, limitados, ni modificados en forma alguna."}
      </v5.PopUp>
    </LoginWrapper>
  );
};

export default LoginForm;
